import React, { useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import { useDidMount } from "@gemlightbox/core-kit";

import { getUserTagManagerInfoCallback } from "src/api";
import { PageContainer, PageHeader } from "src/external-ts/components";
import { useStores } from "src/hooks";
import { CREATE_PAGE, PRODUCT_CREATE_PAGE } from "src/constants";
import { ExtendedMediaModel } from "src/store";
import { MediaList } from "../media-list";
import { aiProductDescriptionMediaLimit } from "./ai-product-description.constants";
import { pushDataLayerEvent } from "src/utils";

import styles from "../../../components/pages/product/product-attributes/product-attributes.module.css";
import WSConnection from "../../../../common/socket";
import { AI_DESCRIPTION_BY_IMAGE } from "src/api/graphql-api/product/product.gql";
import { useMutation } from "urql";

export const AIProductDescription: React.FC = observer(() => {
  const navigate = useNavigate();
  const [AIResult, executeAIMutation] = useMutation(AI_DESCRIPTION_BY_IMAGE);

  const { userStore, modalsStore, localeStore, subscriptionsStore, notificationStore } =
    useStores();

  const selectedMediasRef = useRef<ExtendedMediaModel[]>([]);
  const requestIdRef = useRef("");

  const handleOpenDescriptionSidebar = (initialDescriptions?: string[], infos?: any) => {
    if (!selectedMediasRef.current) return;

    modalsStore.open("AIDescriptionBuilderSidebar", {
      initialTitle: infos.detailedTitle,
      initialProductType: infos.productType,
      initialDescriptions,
      onDescriptionSelect: ({ productType, description, detailedTitle }) => {
        navigate(PRODUCT_CREATE_PAGE.path, {
          state: JSON.stringify({
            type: "ai-description",
            initialImages: toJS(selectedMediasRef.current),
            initialFormValues: {
              detailedTitle,
              productType,
              description,
            },
          }),
        });
      },
    });
  };

  const handleGoBack = () => navigate(-1);

  const handleAIDescriptionProduct = async (medias: ExtendedMediaModel[]) => {
    selectedMediasRef.current = medias;
    const media = medias[0];

    notificationStore.openLoader({
      loaderType: "progress-circle",
      contentClassName: styles.loader,
      progress: 0,
    });

    const variables = { mediaId: media.id, language: userStore.aiLanguage };
    const requestResult = await executeAIMutation(variables);

    notificationStore.closeLoader();
    if (requestResult.error) {
      notificationStore.open({
        title: localeStore.t('app["error-modal"].title'),
        icon: "crossRoundedFilled",
        confirmAppearance: "error",
        confirmText: localeStore.t("common.buttons.ok"),
        cancelText: "",
        onlyConfirm: true,
      });
      return;
    }

    userStore.loadUserMeSilently();

    getUserTagManagerInfoCallback((response) => {
      pushDataLayerEvent({
        event: "gemhub:product:ai_description:received",
        user_id: response.user_id,
        account_type: response.account_type,
        is_trial: response.isTrial,
      });
    });
    var jewelryType = requestResult.data?.aiDescriptionByImage?.jewelryType;
    var productType = jewelryType ? jewelryType.charAt(0).toUpperCase() + jewelryType.slice(1) : "";
    handleOpenDescriptionSidebar(
      [
        requestResult.data?.aiDescriptionByImage?.gptDescription1,
        requestResult.data?.aiDescriptionByImage?.gptDescription2,
        requestResult.data?.aiDescriptionByImage?.gptDescription3,
      ],
      {
        detailedTitle: requestResult.data?.aiDescriptionByImage?.productTitle,
        productType,
      },
    );
  };

  useDidMount(() => {
    const userId = userStore.userMe?.user._id;

    if (!userId) return;

    const progressWS = new WSConnection(`/user/${userId}`, userStore.token);

    progressWS.on("progress/UPDATE", ({ payload }: any) => {
      if (requestIdRef.current === payload.requestID) {
        notificationStore.openLoader({
          loaderType: "progress-circle",
          progress: payload.progress,
          contentClassName: styles.loader,
        });

        if (payload.progress === 100) notificationStore.closeLoader();
      }
    });

    return () => {
      progressWS.destroy();
    };
  });

  if (
    !userStore.isSubscribed &&
    (userStore.userUsage?.countGPTDescriptionsAttemptMonth ?? 0) >=
      subscriptionsStore.limits.countGPTDescription
  )
    return <Navigate to={CREATE_PAGE.path} />;

  return (
    <PageContainer>
      <PageHeader
        title={localeStore.t('create["ai-product-description"].title')}
        onBack={handleGoBack}
      />
      <MediaList
        subTitle={localeStore.t('create["ai-product-description"].subtitle')}
        actionBtnText={localeStore.t('create["ai-product-description"]["action-btn-text"]')}
        limit={aiProductDescriptionMediaLimit}
        onActionClick={handleAIDescriptionProduct}
        withoutSku
        isAIDescription
        type="image"
      />
    </PageContainer>
  );
});

export default AIProductDescription;
